import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useConfirm } from "../context/ConfirmContext";
import { toast } from "sonner";
import { Mail, MessageSquare, Send, Users, Info, CheckCircle2, Clock, MonitorSmartphone, Users2, Search, BookmarkPlus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const inputCls = "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all px-4 py-2.5 text-sm";

export default function Messaging() {
  const [channel, setChannel] = useState("portal");
  const [members, setMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [mode, setMode] = useState("all"); // all | team | selected
  const [teamId, setTeamId] = useState("");
  const [memberQuery, setMemberQuery] = useState("");
  const [postToPortal, setPostToPortal] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [history, setHistory] = useState([]);
  const [config, setConfig] = useState({ email_enabled: false, sms_enabled: false });
  const [sending, setSending] = useState(false);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [tplDialog, setTplDialog] = useState(false);
  const [tplEditing, setTplEditing] = useState(null); // preset being edited, or null for new
  const [tplName, setTplName] = useState("");
  const [tplSubject, setTplSubject] = useState("");
  const [tplBody, setTplBody] = useState("");
  const [savingTpl, setSavingTpl] = useState(false);
  const confirm = useConfirm();

  const loadPresets = async () => {
    try { const { data } = await api.get("/message-presets"); setCustomTemplates(data); }
    catch (err) { /* silent */ }
  };
  const load = async () => {
    const [m, h, c, t] = await Promise.all([
      api.get("/members"), api.get("/messages"), api.get("/messages/config"), api.get("/teams"),
    ]);
    setMembers(m.data); setHistory(h.data); setConfig(c.data); setTeams(t.data);
    if (t.data.length && !teamId) setTeamId(t.data[0].id);
    loadPresets();
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const location = useLocation();
  useEffect(() => {
    const mid = location.state?.memberId;
    const mids = location.state?.memberIds;
    if (Array.isArray(mids) && mids.length) { setMode("selected"); setSelected(new Set(mids)); }
    else if (mid) { setMode("selected"); setSelected(new Set([mid])); }
    if (location.state?.subject) setSubject(location.state.subject);
    if (location.state?.body) setBody(location.state.body);
  }, [location.state]);

  const PRESETS = [
    { key: "reminder", label: "Class reminder", subject: "Reminder: your next class", body: "Hi, just a reminder about our next class. We look forward to seeing you there! Please let us know if you can't make it.\n\nBlessings,\nStreams of Joy Erith London" },
    { key: "encourage", label: "Encouragement", subject: "Keep going — you're doing great!", body: "Hi, we just wanted to encourage you as you continue your course. Keep up the great work and reach out if you need any support.\n\nBlessings,\nStreams of Joy Erith London" },
    { key: "attendance", label: "Attendance nudge", subject: "We've missed you in class", body: "Hi, we noticed you've missed a few classes recently and wanted to check in. We'd love to see you back — is there anything we can help with?\n\nWith love,\nStreams of Joy Erith London" },
    { key: "welcome", label: "Welcome to the course", subject: "Welcome to your course!", body: "Hi, welcome to the course! We're so glad to have you. Log in to the portal any time to see your lessons, sessions and progress.\n\nBlessings,\nStreams of Joy Erith London" },
  ];
  const applyPreset = (val) => {
    if (val.startsWith("custom:")) {
      const t = customTemplates.find((x) => x.id === val.slice(7));
      if (t) { setSubject(t.subject || ""); setBody(t.body || ""); }
      return;
    }
    const p = PRESETS.find((x) => x.key === val);
    if (p) { setSubject(p.subject); setBody(p.body); }
  };

  const openNewTemplate = () => {
    setTplEditing(null); setTplName(""); setTplSubject(subject); setTplBody(body); setTplDialog(true);
  };
  const openEditTemplate = (t) => {
    setTplEditing(t); setTplName(t.name); setTplSubject(t.subject || ""); setTplBody(t.body || ""); setTplDialog(true);
  };
  const saveTemplate = async () => {
    if (!tplName.trim()) return toast.error("Give your template a name");
    if (!tplBody.trim()) return toast.error("Template message can't be empty");
    setSavingTpl(true);
    try {
      const payload = { name: tplName.trim(), subject: tplSubject, body: tplBody };
      if (tplEditing) { await api.put(`/message-presets/${tplEditing.id}`, payload); toast.success("Template updated"); }
      else { await api.post("/message-presets", payload); toast.success("Template saved"); }
      setTplDialog(false); loadPresets();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingTpl(false); }
  };
  const deleteTemplate = async (t) => {
    if (!(await confirm({ title: "Delete template?", description: `Delete the "${t.name}" template? This can't be undone.`, confirmLabel: "Delete template" }))) return;
    try { await api.delete(`/message-presets/${t.id}`); toast.success("Template deleted"); loadPresets(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggle = (id) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n); };

  const filteredMembers = (() => {
    const q = memberQuery.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => {
      const name = `${m.first_name || ""} ${m.last_name || ""}`.toLowerCase();
      return name.includes(q) || (m.email || "").toLowerCase().includes(q) || (m.phone || "").toLowerCase().includes(q);
    });
  })();

  const isPortal = channel === "portal";
  const enabled = isPortal ? true : channel === "email" ? config.email_enabled : config.sms_enabled;

  const currentTeam = teams.find((t) => t.id === teamId);
  const recipientCount = mode === "all" ? members.length : mode === "team" ? (currentTeam?.member_count || 0) : selected.size;

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return toast.error("Message body is required");
    if (mode === "selected" && selected.size === 0) return toast.error("Select at least one recipient");
    setSending(true);
    try {
      const { data } = await api.post("/messages/send", {
        channel, subject, body,
        recipient_ids: mode === "selected" ? [...selected] : [],
        send_to_all: mode === "all",
        send_to_team_id: mode === "team" ? teamId : "",
        post_to_portal: isPortal ? true : postToPortal,
        notify_email: isPortal ? notifyEmail : false,
      });
      if (data.status === "posted") toast.success(`Posted to ${data.posted_count} members' portals`);
      else if (data.status === "simulated") toast.warning(`Simulated for ${data.recipient_count} — add API keys to send for real${data.posted_count ? `; posted to ${data.posted_count} portals` : ""}`);
      else toast.success(`Sent to ${data.sent_count} of ${data.recipient_count}${data.posted_count ? `; posted to ${data.posted_count} portals` : ""}`);
      setSubject(""); setBody(""); setSelected(new Set()); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="sj-page-hero">
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Messaging Center</h1>
        <p style={{ color: "var(--sj-text-2)" }}>Reach your church family by portal, email and text</p>
      </div>

      {!enabled && (
        <div className="flex items-start gap-3 rounded-2xl p-4" style={{ background: "var(--sj-peach)" }} data-testid="messaging-config-banner">
          <Info className="h-5 w-5 mt-0.5 shrink-0" style={{ color: "var(--sj-primary)" }} />
          <div className="text-sm" style={{ color: "var(--sj-text-2)" }}>
            <span className="font-semibold" style={{ color: "var(--sj-text)" }}>{channel === "email" ? "Email (Resend)" : "SMS (Twilio)"} not yet connected.</span>{" "}
            External delivery will be <em>simulated</em>, but messages still appear on members' portals if enabled. Add API keys to send for real.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`${CARD} p-6 lg:col-span-2`}>
          <div className="flex gap-2 mb-6 flex-wrap">
            {[{ k: "portal", label: "Portal", icon: MonitorSmartphone }, { k: "email", label: "Email", icon: Mail }, { k: "sms", label: "SMS", icon: MessageSquare }].map((t) => (
              <button key={t.k} data-testid={`channel-${t.k}`} onClick={() => setChannel(t.k)}
                className="flex items-center gap-2 rounded-full px-5 py-2.5 font-medium border transition-all"
                style={channel === t.k ? { background: "var(--sj-primary)", color: "#fff", borderColor: "var(--sj-primary)" } : { background: "#fff", color: "var(--sj-text-2)", borderColor: "var(--sj-border)" }}>
                <t.icon className="h-4 w-4" />{t.label}
              </button>
            ))}
          </div>

          <form onSubmit={send} className="space-y-4">
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>
                <input type="radio" checked={mode === "all"} onChange={() => setMode("all")} className="accent-[#0B5E4E]" data-testid="send-all-radio" /> All members ({members.length})
              </label>
              <label className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>
                <input type="radio" checked={mode === "team"} onChange={() => setMode("team")} className="accent-[#0B5E4E]" data-testid="send-team-radio" /> A team
              </label>
              <label className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>
                <input type="radio" checked={mode === "selected"} onChange={() => setMode("selected")} className="accent-[#0B5E4E]" data-testid="send-selected-radio" /> Select recipients
              </label>
            </div>

            {mode === "team" && (
              <div className="flex items-center gap-2">
                <Users2 className="h-4 w-4" style={{ color: "var(--sj-secondary)" }} />
                <select data-testid="team-select" value={teamId} onChange={(e) => setTeamId(e.target.value)} className={`${inputCls} max-w-xs`}>
                  {teams.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.member_count})</option>)}
                </select>
              </div>
            )}

            {mode === "selected" && (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--sj-text-3)" }} />
                  <input data-testid="recipient-search" value={memberQuery} onChange={(e) => setMemberQuery(e.target.value)}
                    placeholder="Search members by name, email or phone…"
                    className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 outline-none transition-all pl-9 pr-3 py-2.5 text-sm" />
                </div>
                {selected.size > 0 && (
                  <div className="flex items-center justify-between text-xs" style={{ color: "var(--sj-text-2)" }}>
                    <span data-testid="recipient-selected-count">{selected.size} selected</span>
                    <button type="button" data-testid="recipient-clear" onClick={() => setSelected(new Set())} className="font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>Clear</button>
                  </div>
                )}
                <div className="rounded-xl border p-3 max-h-56 overflow-y-auto space-y-1" style={{ borderColor: "var(--sj-border)" }} data-testid="recipient-picker">
                  {filteredMembers.length === 0 ? (
                    <div className="text-sm text-center py-4" style={{ color: "var(--sj-text-3)" }} data-testid="recipient-no-results">No members match “{memberQuery}”.</div>
                  ) : filteredMembers.map((m) => (
                    <label key={m.id} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-[#EDF2F4] cursor-pointer text-sm">
                      <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggle(m.id)} className="accent-[#17A88A]" data-testid={`recipient-${m.id}`} />
                      {m.first_name} {m.last_name}
                      <span className="text-xs ml-auto" style={{ color: "var(--sj-text-3)" }}>{channel === "sms" ? m.phone : m.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {(channel === "email" || channel === "portal") && (
              <input data-testid="message-subject-input" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} />
            )}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <select data-testid="message-template-select" defaultValue="" onChange={(e) => { applyPreset(e.target.value); e.target.value = ""; }} className={inputCls}>
                  <option value="">Insert a template…</option>
                  <optgroup label="Built-in">
                    {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </optgroup>
                  {customTemplates.length > 0 && (
                    <optgroup label="Your saved templates">
                      {customTemplates.map((t) => <option key={t.id} value={`custom:${t.id}`}>{t.name}</option>)}
                    </optgroup>
                  )}
                </select>
                <button type="button" data-testid="save-as-template-btn" onClick={openNewTemplate}
                  className="flex items-center gap-1.5 rounded-xl px-3 py-2.5 text-sm font-medium border shrink-0 transition-colors hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}>
                  <BookmarkPlus className="h-4 w-4" /> Save as template
                </button>
              </div>
              {customTemplates.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="custom-template-chips">
                  {customTemplates.map((t) => (
                    <span key={t.id} data-testid={`custom-template-chip-${t.id}`} className="inline-flex items-center gap-1 rounded-full pl-3 pr-1.5 py-1 text-xs font-medium border" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                      {t.name}
                      <button type="button" data-testid={`edit-template-${t.id}`} onClick={() => openEditTemplate(t)} title="Edit template" className="p-1 rounded-full hover:bg-[#E3F1EC]"><Pencil className="h-3 w-3" style={{ color: "var(--sj-primary)" }} /></button>
                      <button type="button" data-testid={`delete-template-${t.id}`} onClick={() => deleteTemplate(t)} title="Delete template" className="p-1 rounded-full hover:bg-[#FDE7E7]"><Trash2 className="h-3 w-3" style={{ color: "#B91C1C" }} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <textarea data-testid="message-body-input" required rows={7} placeholder={isPortal ? "Write your announcement for members' portals..." : channel === "email" ? "Write your email..." : "Write your text message..."} value={body} onChange={(e) => setBody(e.target.value)} className={inputCls} />

            {!isPortal && (
              <label className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>
                <input type="checkbox" checked={postToPortal} onChange={(e) => setPostToPortal(e.target.checked)} className="accent-[#17A88A]" data-testid="post-to-portal-checkbox" />
                Also post to members' portal inbox
              </label>
            )}
            {isPortal && (
              <label className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>
                <input type="checkbox" checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)} className="accent-[#17A88A]" data-testid="notify-email-checkbox" />
                Also email members a notification {config.email_enabled ? "" : "(simulated — no email key)"}
              </label>
            )}

            <div className="flex items-center justify-between">
              <span className="text-sm flex items-center gap-1.5" style={{ color: "var(--sj-text-2)" }}><Users className="h-4 w-4" /> {recipientCount} recipient{recipientCount !== 1 ? "s" : ""}</span>
              <button type="submit" disabled={sending} data-testid="send-message-button"
                className="flex items-center gap-2 rounded-full px-6 py-2.5 text-white font-semibold shadow-sm hover:brightness-105 transition-all disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>
                <Send className="h-4 w-4" /> {sending ? "Sending..." : isPortal ? "Post to Portals" : "Send Message"}
              </button>
            </div>
          </form>
        </div>

        <div className={`${CARD} p-6`}>
          <h3 className="font-head text-xl font-semibold mb-4">Recent Campaigns</h3>
          <div className="space-y-3 max-h-[560px] overflow-y-auto pr-1" data-testid="message-history">
            {history.length === 0 && <div className="text-sm text-center py-8" style={{ color: "var(--sj-text-3)" }}>No messages sent yet.</div>}
            {history.map((h) => (
              <div key={h.id} className="rounded-xl border p-4" style={{ borderColor: "var(--sj-border)" }} data-testid={`campaign-${h.id}`}>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider" style={{ color: h.channel === "email" ? "#17A88A" : h.channel === "portal" ? "#0B5E4E" : "#084438" }}>
                    {h.channel === "email" ? <Mail className="h-3.5 w-3.5" /> : h.channel === "portal" ? <MonitorSmartphone className="h-3.5 w-3.5" /> : <MessageSquare className="h-3.5 w-3.5" />}{h.channel}
                  </span>
                  <StatusBadge status={h.status} />
                </div>
                {h.subject && <div className="mt-2 font-semibold text-sm">{h.subject}</div>}
                <p className="mt-1 text-xs line-clamp-2" style={{ color: "var(--sj-text-2)" }}>{h.body}</p>
                <div className="mt-2 text-xs" style={{ color: "var(--sj-text-3)" }}>from {h.sent_by} · {h.recipient_count} recipients{h.posted_count ? ` · ${h.posted_count} portals` : ""} · {new Date(h.created_at).toLocaleDateString('en-GB')}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={tplDialog} onOpenChange={setTplDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-head text-2xl">{tplEditing ? "Edit template" : "Save as template"}</DialogTitle>
            <DialogDescription>{tplEditing ? "Update this saved message template." : "Save this subject and message so you can reuse it any time."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <input data-testid="template-name-input" placeholder="Template name (e.g. Class reminder)" value={tplName} onChange={(e) => setTplName(e.target.value)} className={inputCls} />
            <input data-testid="template-subject-input" placeholder="Subject (optional)" value={tplSubject} onChange={(e) => setTplSubject(e.target.value)} className={inputCls} />
            <textarea data-testid="template-body-textarea" rows={6} placeholder="Message body" value={tplBody} onChange={(e) => setTplBody(e.target.value)} className={inputCls} />
            <DialogFooter>
              <button type="button" onClick={() => setTplDialog(false)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
              <button type="button" data-testid="save-template-btn" disabled={savingTpl} onClick={saveTemplate} className="rounded-full px-6 py-2.5 text-white font-semibold disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>{savingTpl ? "Saving…" : tplEditing ? "Save changes" : "Save template"}</button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    sent: { bg: "#17A88A1A", color: "#17A88A", icon: CheckCircle2 },
    posted: { bg: "#0B5E4E1A", color: "#0B5E4E", icon: CheckCircle2 },
    partial: { bg: "#0844381A", color: "#084438", icon: Info },
    failed: { bg: "#0B5E4E1A", color: "#0B5E4E", icon: Info },
    simulated: { bg: "#56626A1A", color: "#56626A", icon: Clock },
  };
  const s = map[status] || map.simulated;
  const Icon = s.icon;
  return <span className="flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize" style={{ background: s.bg, color: s.color }}><Icon className="h-3 w-3" />{status}</span>;
}
